import { useState } from "react";
import { useSelector } from "react-redux";
import { T } from "react-translator-component";
import {
  Avatar,
  Box,
  Button,
  List,
  Modal,
} from "@mui/material";
import styled from "styled-components";
import { colors } from "../../../common/colors";
import { convertUserImage, isPostCreator } from "../../../util/utils";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AddPostUser from "./AddPostUser";
import TwoButtonModal from "../../../components/TwoButtonModal";
import LoadingModal from "../../../components/LoadingModal";
import apiServices from "../../services/apiServices";
import NoticeBanner from "../NoticeBanner";
import { useAppDispatch } from "../../../redux/store";
import { setErrorObj } from "../../redux/errorReducer";
import SafeTextWithLinks from "../SafeTextWithLinks";

const Container = styled.div`
  margin-top: 7px;
  padding: "0 12px";
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
  border: 2 sol;
`;

interface Props {
  open: boolean;
  post: any;
  privateUsers: any[];
  highlightWords: string[];
  onChange: (users: any[]) => void;
  closeModal(): void;
  readonly: boolean;
}

const PrivateUsersModal = ({ open, post, privateUsers, highlightWords, onChange, closeModal, readonly }: Props) => {
  const dispatch = useAppDispatch();
  const { admin } = useSelector((state: any) => state.adminAuth);
  const [showAddUserModal, setShowAddUserModal] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const [removeUserId, setRemoveUserId] = useState<number | null>(null);

  const [showMessages, setShowMessages] = useState<string[]>([]);
  const [openMessageBanner, setOpenMessageBanner] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleClose = () => {
    setShowAddUserModal(false)
    closeModal()
  }

  const handleRemoveUser = async () => {
    if (readonly || !removeUserId) return false;
    setShowConfirmationModal(false);

    try {
      setIsLoading(true);
      await apiServices.removeUserInPost(removeUserId)
      setShowMessages(T("mng_success_delete_post_private_user"));
      const newPrivateUsers = privateUsers.filter((user: any) => user.id !== removeUserId)
      onChange(newPrivateUsers)
      setRemoveUserId(null)
      setOpenMessageBanner(true)
      return true
    } catch (error: any) {
      dispatch(setErrorObj(error))
      return false
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickRemoveIcon = (id: number) => {
    setRemoveUserId(id)
    setShowConfirmationModal(true)
  }

  const handleCloseConfirmationModal = () => {
    setRemoveUserId(null)
    setShowConfirmationModal(false)
  }

  const handleAddUser = async (user: any) => {
    if (readonly || !isPostCreator(post, admin)) return false

    try {
      const res = await apiServices.addUserInPost(post.id, user.id, user.class_name)
      setShowMessages(T("mng_success_add_post_private_user"))
      setOpenMessageBanner(true)
      const newPrivateUsers = privateUsers.map((user: any) => user)
      const resPrivateUsers = res.post_private_user
      const privater = resPrivateUsers.privater_type === "User" ?
        resPrivateUsers.privater?.user : resPrivateUsers.privater?.admin_user
      const newPrivater = {
        id: resPrivateUsers.id,
        privater_id: resPrivateUsers.privater_id,
        privater_type: resPrivateUsers.privater_type,
        nickname: privater?.nickname,
        image: privater?.image
      }
      newPrivateUsers.push(newPrivater)
      onChange(newPrivateUsers)
      return true
    } catch (error: any) {
      console.error(error)
      dispatch(setErrorObj(error))
      return false
    }
  }

  const displayPrivateUser = (privateUser: any) => {
    const isCreator = post.creator_type === privateUser.privater_type && post.creator_id === privateUser?.privater_id

    return (
      <Box key={privateUser.id} sx={style.itemContainer}>
        <Avatar sx={style.userImage} src={convertUserImage(privateUser?.image)} />
        <p style={{ ...style.nameText, fontWeight: "normal" }}>{privateUser?.nickname}</p>
        {isCreator && (
          <p style={style.creatorNotice}>{T("mng_post_creator")}</p>
        )}
        {(!readonly && isPostCreator(post, admin) && !isCreator) && (
          <RemoveCircleOutlineIcon onClick={() => handleClickRemoveIcon(privateUser.id)} sx={style.removeIcon} />)
        }
      </Box>
    )
  }

  return (
    <>
      <LoadingModal visible={isLoading}/>
      <Modal open={open} onClose={handleClose}>
        <Box sx={style.container}>
          {showAddUserModal ? (
            <AddPostUser
              onClose={() => setShowAddUserModal(false)}
              post={post}
              privateUsers={privateUsers}
              onChange={handleAddUser}
              readonly={readonly}
            />
          ) : (
            <Container>
              <SafeTextWithLinks
                text={post.title}
                highlightWords={highlightWords}
                sx={style.title}
              />

              <div style={style.userList}>
                <List
                  sx={{
                    "& .MuiListItemButton-root:hover": {
                      bgcolor: colors.farm_gray10,
                      "&, & .MuiListItemIcon-root": {
                        color: "black",
                      },
                      "&$focusVisible": {
                        backgroundColor: colors.farm_gray10,
                      },
                      "& .MuiListItemButton-root:touch": {
                        bgcolor: colors.farm_gray10,
                        "&, & .MuiListItemIcon-root": {
                          color: "black",
                        },
                      },
                    },
                    padding: "20px"
                  }}
                >
                  {privateUsers.map((user: any) => displayPrivateUser(user))}
                </List>
              </div>
              <Box sx={style.buttonBox}>
                <Button
                  sx={style.noButton}
                  onClick={() => setShowAddUserModal(true)}
                  disabled={readonly || !isPostCreator(post, admin)}
                >
                  {T("mng_add_post_private_user")}
                </Button>
                <Button
                  sx={style.buttonStyle}
                  onClick={() => closeModal()}
                >
                  {T('mng_close')}
                </Button>
              </Box>
            </Container>
          )}
        </Box>
      </Modal>

      <TwoButtonModal
        message={T("mng_confirmation_delete_post_private_user")[0]}
        onContinue={() => handleRemoveUser()}
        open={showConfirmationModal}
        onClose={() => handleCloseConfirmationModal()}
        noText={T("mng_no")[0]}
      />

      <NoticeBanner
        open={openMessageBanner}
        onClose={() => setOpenMessageBanner(false)}
        messageList={showMessages}
      />
    </>
  );
};

export default PrivateUsersModal;

const style = {
  itemContainer: {
    display: "flex",
    alignItems: "center",
    marginRight: "16px",
    marginBottom: "16px",
    borderBottomStyle: "solid" as const,
    borderBottomColor: colors.farm_gray100,
    borderBottomWidth: 1,
    padding: "5px 0px",
  },
  removeIcon: {
    fontSize: "34px",
    cursor: "pointer",
    color: colors.farm_red600,
  },
  userImage: {
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    objectFit: "cover",
    marginLeft: "10px",
  },
  nameText: {
    fontSize: 14,
    fontWeight: 600,
    marginLeft: "10px",
    marginBottom: "0px",
    flex: 1,
    textAlign: "left" as const,
  },
  creatorNotice: {
    fontSize: 14,
    marginLeft: "10px",
    marginBottom: "0px",
    border: `2px solid ${colors.farm_green200}`,
    borderRadius: "10px",
    padding: "3px 10px",
  },
  buttonBox: {
    display: "flex",
    justifyContent: "space-between"
  },
  buttonStyle: {
    cursor: "pointer",
    width: "50%",
    textAlign: "center",
    border: "0.5px solid gray",
    borderRight: "none",
    borderBottom: "none",
    borderRadius: 0,
    padding: "10px",
    color: colors.farm_blue200,
    fontWeight: "bold",
  },
  noButton: {
    cursor: "pointer",
    width: "50%",
    textAlign: "center",
    border: "0.5px solid gray",
    borderRight: "none",
    borderBottom: "none",
    borderRadius: 0,
    padding: "10px",
    color: colors.farm_blue200,
    fontWeight: "bold",
    borderLeft: "none"
  },
  container: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "30%",
    minWidth: "400px",
    maxHeight: "90%",
    bgcolor: "background.paper",
    boxShadow: 24,
    padding: 0,
    borderRadius: "15px",
    textAlign: "center",
  },
  title: {
    textAlign: "center",
    marginTop: 2,
    fontWeight: 700,
    marginBottom: "20px"
  },
  userList: {
    maxHeight: "calc(100vh*0.9*0.7 - 90px)",
    overflowY: "auto" as const,
  },
}
